import type { GUnit } from 'interfaces/IGlobalCatalogue';
import { ZoneLinkType, type IInput } from 'interfaces/IInput';
import {
  InputStepType,
  type IInputStep,
  type IInputStepSoilInput,
} from 'interfaces/IInputStep';
import type { IInputTask, InputTaskCategory } from 'interfaces/IInputTask';
import { getInputDisplayName, getInputYear } from 'utils/FarmUtils';
import {
  convertToBulkDensityKgPerL,
  convertToHectares,
  convertToKg,
  convertToLitres,
} from 'utils/unitConversion';
import { PLAN_AND_ACTUAL_CATEGORIES, resolveAreaHa } from './insightUtils';

/* ═══════════════════════════════════════════════════════════════
   Nutrient types and metadata
   ═══════════════════════════════════════════════════════════════ */

/** All nutrient field keys on IInputStepSoilInput. */
export type NutrientKey =
  | 'nutrientN'
  | 'nutrientP'
  | 'nutrientK'
  | 'nutrientS'
  | 'nutrientCa'
  | 'nutrientMg'
  | 'nutrientNa'
  | 'nutrientB'
  | 'nutrientCu'
  | 'nutrientFe'
  | 'nutrientMn'
  | 'nutrientZn'
  | 'nutrientMo'
  | 'nutrientCl'
  | 'nutrientSi'
  | 'nutrientNi'
  | 'nutrientCo';

/** Aggregated nutrient totals in kg. */
export type NutritionTally = Record<NutrientKey, number>;

/**
 * Display order for nutrients: macronutrients first, then micronutrients.
 * Follows standard agronomic reporting convention.
 */
export const NUTRIENT_DISPLAY_ORDER: NutrientKey[] = [
  'nutrientN',
  'nutrientP',
  'nutrientK',
  'nutrientS',
  'nutrientCa',
  'nutrientMg',
  'nutrientNa',
  'nutrientB',
  'nutrientCu',
  'nutrientFe',
  'nutrientMn',
  'nutrientZn',
  'nutrientMo',
  'nutrientCl',
  'nutrientSi',
  'nutrientNi',
  'nutrientCo',
];

/** Human-readable labels for each nutrient field. */
export const NUTRIENT_LABELS: Record<NutrientKey, string> = {
  nutrientN: 'N (Nitrogen)',
  nutrientP: 'P (Phosphorus)',
  nutrientK: 'K (Potassium)',
  nutrientS: 'S (Sulfur)',
  nutrientCa: 'Ca (Calcium)',
  nutrientMg: 'Mg (Magnesium)',
  nutrientNa: 'Na (Sodium)',
  nutrientB: 'B (Boron)',
  nutrientCu: 'Cu (Copper)',
  nutrientFe: 'Fe (Iron)',
  nutrientMn: 'Mn (Manganese)',
  nutrientZn: 'Zn (Zinc)',
  nutrientMo: 'Mo (Molybdenum)',
  nutrientCl: 'Cl (Chlorine)',
  nutrientSi: 'Si (Silicon)',
  nutrientNi: 'Ni (Nickel)',
  nutrientCo: 'Co (Cobalt)',
};

/** Short symbol labels used in the pie chart hover tooltip. */
export const NUTRIENT_SHORT_LABELS: Record<NutrientKey, string> = {
  nutrientN: 'N',
  nutrientP: 'P',
  nutrientK: 'K',
  nutrientS: 'S',
  nutrientCa: 'Ca',
  nutrientMg: 'Mg',
  nutrientNa: 'Na',
  nutrientB: 'B',
  nutrientCu: 'Cu',
  nutrientFe: 'Fe',
  nutrientMn: 'Mn',
  nutrientZn: 'Zn',
  nutrientMo: 'Mo',
  nutrientCl: 'Cl',
  nutrientSi: 'Si',
  nutrientNi: 'Ni',
  nutrientCo: 'Co',
};

/**
 * Pie-chart fill colours for each nutrient.
 *
 * Macronutrients (N–Na) use saturated tones; micronutrients use softer
 * variants. These are chart-specific palette values, not mapped to CSS
 * design tokens.
 */
export const NUTRIENT_PIE_COLORS: Record<NutrientKey, string> = {
  nutrientN:  '#22c55e', // green-500
  nutrientP:  '#3b82f6', // blue-500
  nutrientK:  '#f97316', // orange-500
  nutrientS:  '#eab308', // yellow-500
  nutrientCa: '#a855f7', // purple-500
  nutrientMg: '#06b6d4', // cyan-500
  nutrientNa: '#ef4444', // red-500
  nutrientB:  '#84cc16', // lime-500
  nutrientCu: '#d97706', // amber-600
  nutrientFe: '#b45309', // amber-700
  nutrientMn: '#7c3aed', // violet-700
  nutrientZn: '#0ea5e9', // sky-500
  nutrientMo: '#059669', // emerald-600
  nutrientCl: '#c026d3', // fuchsia-600
  nutrientSi: '#78716c', // stone-500
  nutrientNi: '#0f766e', // teal-700
  nutrientCo: '#1d4ed8', // blue-700
};

/* ═══════════════════════════════════════════════════════════════
   Nutrition tally computation
   ═══════════════════════════════════════════════════════════════ */

export const emptyTally = (): NutritionTally => ({
  nutrientN: 0,
  nutrientP: 0,
  nutrientK: 0,
  nutrientS: 0,
  nutrientCa: 0,
  nutrientMg: 0,
  nutrientNa: 0,
  nutrientB: 0,
  nutrientCu: 0,
  nutrientFe: 0,
  nutrientMn: 0,
  nutrientZn: 0,
  nutrientMo: 0,
  nutrientCl: 0,
  nutrientSi: 0,
  nutrientNi: 0,
  nutrientCo: 0,
});

/** A contributing SoilInput step: the area it was applied to and its nutrient kg. */
export interface StepNutrientContribution {
  areaHa: number;
  kg: Partial<NutritionTally>;
}

/**
 * Nutrient kg contributed by one SoilInput step, or null when the step
 * contributes nothing (not a SoilInput, no area, no rate or no nutrients).
 *
 * ── Formula ──────────────────────────────────────────────────────────────────
 * nutrientX fields store the concentration as a percentage (%).
 * Per-step contribution (kg) depends on the product's physical state:
 *
 *   Solid (isLiquid = false):
 *     step_kg = (pct / 100) × rateKg × areaHa
 *     rateKg  = rate converted to kg/ha (appUnit → kg; unrecognised → raw)
 *
 *   Liquid (isLiquid = true):
 *     step_kg = (pct / 100) × rateL × bulkDensity_kg_L × areaHa
 *     rateL   = rate converted to L/ha (appUnit → L; unrecognised → raw)
 *     bulkDensity_kg_L = bulkDensity converted to kg/L
 *                        (unrecognised unit → raw value; null/0 → defaults to 1)
 *
 * ── Area resolution ──────────────────────────────────────────────────────────
 * areaHa = step.area → task.area → zone.zoneArea (first non-null value wins),
 * so variable-rate zones and part-paddock applications stay exact.
 */
export const computeStepNutrientKg = (
  step: IInputStep,
  task: IInputTask,
  input: IInput,
  gUnits: GUnit[]
): StepNutrientContribution | null => {
  if (step.stepType !== InputStepType.SoilInput) return null;

  const s = step as IInputStepSoilInput;
  const areaHa = resolveAreaHa(step, task, input, gUnits);
  if (areaHa <= 0) return null;

  const rate = s.rate ?? 0;
  if (rate <= 0) return null;

  // Normalise rate to kg/ha (solid) or L/ha (liquid).
  // convertToKg / convertToLitres return null for unrecognised units;
  // fall back to the raw value (treated as already in kg or L per ha).
  const rateNorm: number = s.isLiquid
    ? (convertToLitres(rate, s.appUnit, gUnits) ?? rate)
    : (convertToKg(rate, s.appUnit, gUnits) ?? rate);

  // massMultiplier converts L/ha → kg/ha for liquid fertilisers.
  // For solid fertilisers it is always 1.
  //
  // Note on unit source: in principle all unit conversions should be
  // driven by GUnit multipliers (the primary path in unitConversion.ts).
  // However, bulkDensityUnit is not yet catalogued in GUnit, so the
  // GUnit primary path will always miss and fall through to the
  // string-based fallback. Existing records likely have bulkDensityUnit
  // = null, which means the raw bulkDensity value is already in kg/L.
  // Where a unit is explicitly set (e.g. "g/mL", "kg/m³") the string
  // alias table converts it to kg/L before use. Unrecognised unit
  // strings fall back to the raw value (assumed kg/L).
  // bulkDensity of 0 or null → default to 1 (as per formula spec).
  let massMultiplier = 1;
  if (s.isLiquid) {
    const bulkDRaw = s.bulkDensity;
    const bulkDConverted =
      bulkDRaw != null
        ? (convertToBulkDensityKgPerL(bulkDRaw, s.bulkDensityUnit, gUnits) ??
            bulkDRaw)
        : null;
    massMultiplier =
      bulkDConverted != null && bulkDConverted > 0 ? bulkDConverted : 1;
  }

  const kg: Partial<NutritionTally> = {};
  let contributed = false;
  for (const key of NUTRIENT_DISPLAY_ORDER) {
    const pct = s[key] ?? 0;
    if (pct <= 0) continue;
    kg[key] = (pct / 100) * rateNorm * massMultiplier * areaHa;
    contributed = true;
  }
  return contributed ? { areaHa, kg } : null;
};

/**
 * Aggregate nutrient totals (kg) across all SoilInput steps in the given
 * inputs. See computeStepNutrientKg for the underlying formula.
 *
 * All inputs → tasks → steps are flattened into a single loop with no
 * grouping. Each SoilInput step contributes independently and its result is
 * added directly to the running total — no averaging, weighting, or
 * de-duplication occurs at this layer.
 *
 * Pure function — no side effects, no hooks, no Redux.
 */
export const computeNutritionTally = (
  inputs: IInput[],
  gUnits: GUnit[],
  categories: Set<InputTaskCategory>
): NutritionTally => {
  const tally = emptyTally();
  for (const input of inputs) {
    for (const task of input.tasks) {
      if (!categories.has(task.category)) continue;
      for (const step of task.steps) {
        const contribution = computeStepNutrientKg(step, task, input, gUnits);
        if (!contribution) continue;
        for (const key of NUTRIENT_DISPLAY_ORDER) {
          tally[key] += contribution.kg[key] ?? 0;
        }
      }
    }
  }
  return tally;
};

/** A contributing step applied to less than its plan's paddock area. */
export interface PartPaddockApplication {
  inputId: string;
  category: InputTaskCategory;
  /** Product name (step.item). */
  productName: string;
  /** Area the step was applied to (ha). */
  areaHa: number;
  /** Paddock area of the step's plan (ha). */
  planAreaHa: number;
  /** Application rate as entered, per ha. */
  rate: number;
  /** Rate unit as entered (e.g. "kg", "L"), per ha. Null when not recorded. */
  rateUnit: string | null;
}

/** One plan's nutrition rate on its own paddock area. */
export interface PlanNutritionPerHa {
  inputId: string;
  /** Paddock the plan is linked to, or null when it has no paddock link. */
  paddockId: string | null;
  /** Plan label for display, e.g. "2025 Wheat". */
  label: string;
  /** Divisor for this plan: zone.zoneArea in ha (see computeNutritionPerHa). */
  areaHa: number;
  /** Nutrient kg from this plan's steps in the requested categories. */
  kg: NutritionTally;
  /** kg ÷ areaHa. */
  perHa: NutritionTally;
}

export interface NutritionPerHaResult {
  /** Nutrient rates (kg/ha) for the scope. */
  perHa: NutritionTally;
  /** Sum of paddock areas (ha) the rates are averaged over. */
  areaHa: number;
  /** Plans with at least one contributing step in Plan or Actual. */
  plans: PlanNutritionPerHa[];
  /** Contributing steps in the requested categories applied to less than their plan's area. */
  partPaddockApplications: PartPaddockApplication[];
}

export const resolvePaddockId = (input: IInput): string | null =>
  input.zone?.links?.find((l) => l.linkType === ZoneLinkType.Paddock)
    ?.linkId ?? null;

/**
 * One task's nutrient rates (kg/ha), on the same basis as the Nutrition lens
 * (computeNutritionPerHa): each SoilInput step's kg on its own area, divided
 * by the plan's paddock area — so a plan's task captions add up to the
 * figure the lens shows for it. A plan with no paddock area falls back to
 * the task's largest contributing step area.
 *
 * @returns Only nutrients the task applies; empty when it applies none.
 */
export const computeTaskNutritionPerHa = (
  task: IInputTask,
  input: IInput,
  gUnits: GUnit[]
): Partial<NutritionTally> => {
  const kg: Partial<NutritionTally> = {};
  let largestStepAreaHa = 0;
  for (const step of task.steps ?? []) {
    const contribution = computeStepNutrientKg(step, task, input, gUnits);
    if (!contribution) continue;
    largestStepAreaHa = Math.max(largestStepAreaHa, contribution.areaHa);
    for (const key of NUTRIENT_DISPLAY_ORDER) {
      const value = contribution.kg[key];
      if (value) kg[key] = (kg[key] ?? 0) + value;
    }
  }
  if (largestStepAreaHa <= 0) return {};

  const planAreaHa = resolvePlanAreaHa(input, gUnits);
  const areaHa = planAreaHa > 0 ? planAreaHa : largestStepAreaHa;
  const perHa: Partial<NutritionTally> = {};
  for (const key of NUTRIENT_DISPLAY_ORDER) {
    const value = kg[key];
    if (value) perHa[key] = value / areaHa;
  }
  return perHa;
};

const resolvePlanLabel = (input: IInput): string => {
  const year = getInputYear(input);
  const name = getInputDisplayName(input);
  return year != null ? `${year} ${name}` : name;
};

/** Paddock area of a plan in ha — zone.zoneArea converted from its own unit. */
export const resolvePlanAreaHa = (input: IInput, gUnits: GUnit[]): number => {
  const value = input.zone?.zoneArea ?? 0;
  if (value <= 0) return 0;
  return convertToHectares(value, input.zone?.areaUnit, gUnits) ?? value;
};

/**
 * Aggregate nutrient rates (kg/ha) for the given inputs, as shown by the
 * Nutrition Tally panel.
 *
 * ── Divisor ──────────────────────────────────────────────────────────────────
 * Nutrient kg is still computed per step on the step's own area (see
 * computeStepNutrientKg), but the rate divides by the plan's paddock area
 * (zone.zoneArea), counted once per plan — not by the area of every
 * application. Two passes over the same 100 ha add up rather than average.
 *
 * - Each plan's rate = that plan's kg ÷ its own paddock area.
 * - Plans on the same paddock (double cropping, several years) add their
 *   rates: the result is the cumulative rate on ground inside every plan's
 *   boundary. Paddock area is not summed across those plans — it is the same
 *   ground each time.
 * - Across different paddocks, paddock rates are area-weighted by each
 *   paddock's largest plan area. (The Right Panel only runs at paddock or
 *   plan scope, so in practice there is one paddock.)
 * - A plan with no paddock area falls back to its largest contributing
 *   step area.
 *
 * Which plans count is decided from Plan and Actual steps together, whatever
 * `categories` is, so Plan and Actual always divide by the same area.
 *
 * Pure function — no side effects, no hooks, no Redux.
 */
export const computeNutritionPerHa = (
  inputs: IInput[],
  gUnits: GUnit[],
  categories: Set<InputTaskCategory>
): NutritionPerHaResult => {
  const plans: PlanNutritionPerHa[] = [];
  const partPaddockApplications: PartPaddockApplication[] = [];

  for (const input of inputs) {
    const kg = emptyTally();
    const partPaddockSteps: Omit<PartPaddockApplication, 'planAreaHa'>[] = [];
    let largestStepAreaHa = 0;

    for (const task of input.tasks) {
      if (!PLAN_AND_ACTUAL_CATEGORIES.has(task.category)) continue;
      const isRequested = categories.has(task.category);
      for (const step of task.steps) {
        const contribution = computeStepNutrientKg(step, task, input, gUnits);
        if (!contribution) continue;
        largestStepAreaHa = Math.max(largestStepAreaHa, contribution.areaHa);
        if (!isRequested) continue;

        for (const key of NUTRIENT_DISPLAY_ORDER) {
          kg[key] += contribution.kg[key] ?? 0;
        }
        const s = step as IInputStepSoilInput;
        partPaddockSteps.push({
          inputId: input.id,
          category: task.category,
          productName: s.item,
          areaHa: contribution.areaHa,
          rate: s.rate ?? 0,
          rateUnit: s.appUnit?.unit ?? null,
        });
      }
    }

    // No contributing step in Plan or Actual — the plan does not count.
    if (largestStepAreaHa <= 0) continue;

    const zoneAreaHa = resolvePlanAreaHa(input, gUnits);
    const areaHa = zoneAreaHa > 0 ? zoneAreaHa : largestStepAreaHa;

    const perHa = emptyTally();
    for (const key of NUTRIENT_DISPLAY_ORDER) {
      perHa[key] = kg[key] / areaHa;
    }

    plans.push({
      inputId: input.id,
      paddockId: resolvePaddockId(input),
      label: resolvePlanLabel(input),
      areaHa,
      kg,
      perHa,
    });

    for (const step of partPaddockSteps) {
      if (step.areaHa < areaHa) {
        partPaddockApplications.push({ ...step, planAreaHa: areaHa });
      }
    }
  }

  // Group by paddock: rates add within a paddock, area is taken once.
  // A plan with no paddock link is its own group.
  const paddocks = new Map<string, { areaHa: number; perHa: NutritionTally }>();
  for (const plan of plans) {
    const groupKey = plan.paddockId ?? `input:${plan.inputId}`;
    const group = paddocks.get(groupKey) ?? {
      areaHa: 0,
      perHa: emptyTally(),
    };
    group.areaHa = Math.max(group.areaHa, plan.areaHa);
    for (const key of NUTRIENT_DISPLAY_ORDER) {
      group.perHa[key] += plan.perHa[key];
    }
    paddocks.set(groupKey, group);
  }

  const perHa = emptyTally();
  let areaHa = 0;
  paddocks.forEach((group) => {
    areaHa += group.areaHa;
    for (const key of NUTRIENT_DISPLAY_ORDER) {
      perHa[key] += group.perHa[key] * group.areaHa;
    }
  });
  if (areaHa > 0) {
    for (const key of NUTRIENT_DISPLAY_ORDER) {
      perHa[key] /= areaHa;
    }
  }

  return { perHa, areaHa, plans, partPaddockApplications };
};

